import React from 'react';
import { FiArrowUp, FiArrowDown, FiAlertCircle, FiCheckCircle, FiTarget, FiZap } from 'react-icons/fi';

const HighlightCard = ({ item }) => {
  const isPositive = item.direction === 'up'
    ? !item.label.toLowerCase().includes('reaction')
    : item.label.toLowerCase().includes('reaction');

  return (
    <div className="bg-white/60 backdrop-blur rounded-xl p-3 flex flex-col gap-1 min-w-0">
      <span className="text-[11px] text-gray-500 font-medium">{item.label}</span>
      <div className="flex items-end gap-2">
        <span className="text-xl font-bold text-gray-900">{item.value}</span>
        <span className={`flex items-center gap-0.5 text-[10px] font-semibold mb-0.5 ${isPositive ? 'text-green-600' : 'text-orange-600'}`}>
          {item.direction === 'up' ? <FiArrowUp className="h-3 w-3" /> : <FiArrowDown className="h-3 w-3" />}
          {item.change}
        </span>
      </div>
    </div>
  );
};

const WeeklyDigest = ({ digest }) => {
  const safe = digest || {};
  const highlights = Array.isArray(safe.highlights) ? safe.highlights : [];
  const pipeline = safe.pipeline || { awaitingResponse: 0, oldestWaiting: 0, activeInterviews: 0 };
  const suggestions = Array.isArray(safe.suggestions) ? safe.suggestions : [];

  return (
    <div className="rounded-2xl overflow-hidden shadow-lg border border-cyan-100"
      style={{ background: 'linear-gradient(135deg, #ecfeff 0%, #eff6ff 40%, #f0fdf4 100%)' }}>

      <div className="px-6 pt-5 pb-0 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="p-2 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg shadow-sm">
            <FiZap className="h-4 w-4 text-white" />
          </div>
          <div>
            <h2 className="text-base font-bold text-gray-900">Weekly Coaching Digest</h2>
            <p className="text-xs text-gray-500">Week of {safe.weekRange || '—'}</p>
          </div>
        </div>
      </div>

      <div className="px-6 py-5 space-y-5">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {highlights.length > 0 ? highlights.map((h, i) => (
              <HighlightCard key={i} item={h} />
            )) : (
              <div className="col-span-full text-center text-xs text-gray-500 py-4">
                Highlights will appear once you have some weekly activity.
              </div>
            )}
          </div>

          <div className="bg-white/70 backdrop-blur rounded-xl p-4">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-blue-400 mb-2">Pipeline</p>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-lg font-bold text-blue-700">{pipeline.awaitingResponse}</div>
                <div className="text-[10px] text-blue-500 leading-tight">Awaiting<br/>response</div>
              </div>
              <div>
                <div className="text-lg font-bold text-amber-600">{pipeline.oldestWaiting}d</div>
                <div className="text-[10px] text-amber-500 leading-tight">Oldest<br/>waiting</div>
              </div>
              <div>
                <div className="text-lg font-bold text-green-700">{pipeline.activeInterviews}</div>
                <div className="text-[10px] text-green-500 leading-tight">Active<br/>interviews</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white/70 backdrop-blur rounded-xl p-4 flex items-start gap-3">
            <div className="p-1.5 bg-green-100 rounded-lg flex-shrink-0 mt-0.5">
              <FiCheckCircle className="h-4 w-4 text-green-600" />
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-green-600 mb-1">What&apos;s Working</p>
              <p className="text-xs text-gray-700 leading-relaxed">{safe.whatsWorking || 'Keep going — small steady steps compound.'}</p>
            </div>
          </div>

          <div className="bg-white/70 backdrop-blur rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2.5">
              <div className="p-1.5 bg-amber-100 rounded-lg flex-shrink-0">
                <FiTarget className="h-4 w-4 text-amber-600" />
              </div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-600">Suggested Focus</p>
            </div>
            <div className="space-y-2">
              {suggestions.map((s, i) => (
                <div key={i} className="flex items-start gap-2 text-xs text-gray-600">
                  <FiAlertCircle className="h-3.5 w-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <span className="leading-relaxed">{s}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeeklyDigest;
